import { Injectable } from '@angular/core';
import Dexie, { Table } from 'dexie';

import {
  OfflineDetailEintrag,
  OfflineKategorieEintrag,
  OfflineListeEintrag,
  OfflineMessstelleEintrag,
  OfflineMetaEintrag,
  SyncKonfliktEintrag,
  SyncQueueEintrag
} from '../../Models/offline.model';

/**
 * Der lokale Datenbestand für die Arbeit ohne Netz.
 *
 * Bewusst IndexedDB (über Dexie) und nicht der Cache des Service Workers:
 * hier liegen keine Antworten, sondern Fachdaten, die vor Ort GEÄNDERT
 * werden. Ein HTTP-Cache kann das nicht – er kennt nur ganze Antworten und
 * keine Prüfpunkte, keine Warteschlange und keine Änderungsstände.
 *
 * Der Service Worker macht weiterhin die Anwendung selbst offline
 * verfügbar (HTML, JavaScript, Bilder); die Trennung ist Absicht.
 *
 * Tabellen:
 *
 * | Tabelle       | Inhalt                                                  |
 * |---------------|---------------------------------------------------------|
 * | `listen`      | offline verfügbare Inbetriebnahmen (Kopfdaten)          |
 * | `messstellen` | Zeilen für Listenansicht, Suche, Filter, Blättern       |
 * | `details`     | vollständige Messstellen inkl. Prüfpunkten und Verlauf  |
 * | `kategorien`  | Kategorien samt Abschnitten und Prüfpunkt-Vorlagen      |
 * | `syncQueue`   | erfasste Änderungen, die noch zum Server müssen         |
 * | `konflikte`   | Änderungen, die eine Entscheidung brauchen              |
 * | `meta`        | Download- und Sync-Metadaten                            |
 *
 * WICHTIG: Diese Datenbank wird beim Abmelden NICHT geleert. Ein abgelaufener
 * Token oder ein versehentlicher Logout darf einen Tag Arbeit auf der Anlage
 * nicht vernichten. Entfernt wird ein Bestand nur, wenn der Benutzer es
 * ausdrücklich verlangt – und auch dann nur, wenn nichts mehr aussteht.
 */
export class ContainuelOfflineDb extends Dexie {
  listen!: Table<OfflineListeEintrag, number>;
  messstellen!: Table<OfflineMessstelleEintrag, number>;
  details!: Table<OfflineDetailEintrag, number>;
  kategorien!: Table<OfflineKategorieEintrag, number>;
  syncQueue!: Table<SyncQueueEintrag, string>;
  konflikte!: Table<SyncKonfliktEintrag, string>;
  meta!: Table<OfflineMetaEintrag, string>;

  constructor() {
    super('ContainuelInbetriebnahmeOffline');

    this.version(1).stores({
      listen: 'id, downloadedAtUtc, projektId',

      // Der zusammengesetzte Index trägt die Listenansicht: alle Messstellen
      // einer Inbetriebnahme in Zeilenreihenfolge, ohne Sortieren im Speicher.
      messstellen: 'id, messstellenListeId, [messstellenListeId+rowNumber]',

      details: 'id, messstellenListeId',

      kategorien: 'id',

      // `createdAtUtc` ist die Abarbeitungsreihenfolge: ein offline angelegter
      // Prüfpunkt muss vor der Änderung seines Ergebnisses beim Server sein.
      syncQueue: 'id, messstellenListeId, createdAtUtc, typ',

      konflikte: 'id, messstellenListeId, erkanntAmUtc',

      meta: 'key'
    });
  }
}

/**
 * Zugriff auf den lokalen Bestand.
 *
 * Alles, was Daten UND Warteschlange zugleich anfasst, läuft hier durch eine
 * Dexie-Transaktion. Das ist keine Förmlichkeit: bräche der Vorgang zwischen
 * beidem ab, stünde die Änderung in der Oberfläche, ginge aber nie zum Server
 * – oder umgekehrt.
 */
@Injectable({ providedIn: 'root' })
export class OfflineDbService {
  readonly db = new ContainuelOfflineDb();

  /**
   * Ist IndexedDB überhaupt benutzbar? In einem privaten Fenster oder bei
   * gesperrtem Speicher kann der Zugriff scheitern. Dann arbeitet die
   * Anwendung wie bisher rein online weiter, statt mit Fehlern um sich zu
   * werfen.
   */
  private verfuegbarkeit: Promise<boolean> | null = null;

  istVerfuegbar(): Promise<boolean> {
    if (!this.verfuegbarkeit) {
      this.verfuegbarkeit = this.db
        .open()
        .then(() => true)
        .catch(() => false);
    }

    return this.verfuegbarkeit;
  }

  // ── Listen ──

  async ladeListen(): Promise<OfflineListeEintrag[]> {
    if (!(await this.istVerfuegbar())) {
      return [];
    }

    return this.db.listen.toArray();
  }

  async ladeListe(id: number): Promise<OfflineListeEintrag | undefined> {
    if (!(await this.istVerfuegbar())) {
      return undefined;
    }

    return this.db.listen.get(id);
  }

  async istOfflineVerfuegbar(id: number): Promise<boolean> {
    return !!(await this.ladeListe(id));
  }

  // ── Messstellen ──

  async ladeMessstellen(listeId: number): Promise<OfflineMessstelleEintrag[]> {
    if (!(await this.istVerfuegbar())) {
      return [];
    }

    return this.db.messstellen
      .where('[messstellenListeId+rowNumber]')
      .between([listeId, Dexie.minKey], [listeId, Dexie.maxKey])
      .toArray();
  }

  async ladeDetail(messstelleId: number): Promise<OfflineDetailEintrag | undefined> {
    if (!(await this.istVerfuegbar())) {
      return undefined;
    }

    return this.db.details.get(messstelleId);
  }

  // ── Kategorien ──

  async ladeKategorien(): Promise<OfflineKategorieEintrag[]> {
    if (!(await this.istVerfuegbar())) {
      return [];
    }

    return this.db.kategorien.orderBy('id').toArray();
  }

  // ── Warteschlange ──

  /** Alle ausstehenden Änderungen, in Erfassungsreihenfolge. */
  async ladeWarteschlange(listeId?: number): Promise<SyncQueueEintrag[]> {
    if (!(await this.istVerfuegbar())) {
      return [];
    }

    const alle = await this.db.syncQueue.orderBy('createdAtUtc').toArray();

    return listeId == null
      ? alle
      : alle.filter(e => e.messstellenListeId === listeId);
  }

  async zaehleAusstehend(listeId?: number): Promise<number> {
    if (!(await this.istVerfuegbar())) {
      return 0;
    }

    return listeId == null
      ? this.db.syncQueue.count()
      : this.db.syncQueue.where('messstellenListeId').equals(listeId).count();
  }

  async ladeKonflikte(listeId?: number): Promise<SyncKonfliktEintrag[]> {
    if (!(await this.istVerfuegbar())) {
      return [];
    }

    return listeId == null
      ? this.db.konflikte.orderBy('erkanntAmUtc').toArray()
      : this.db.konflikte.where('messstellenListeId').equals(listeId).toArray();
  }

  async zaehleKonflikte(listeId?: number): Promise<number> {
    if (!(await this.istVerfuegbar())) {
      return 0;
    }

    return listeId == null
      ? this.db.konflikte.count()
      : this.db.konflikte.where('messstellenListeId').equals(listeId).count();
  }

  // ── Metadaten ──

  async lies<T>(key: string, standard: T): Promise<T> {
    if (!(await this.istVerfuegbar())) {
      return standard;
    }

    const eintrag = await this.db.meta.get(key);

    return eintrag ? (eintrag.value as T) : standard;
  }

  async schreibe(key: string, value: any): Promise<void> {
    if (!(await this.istVerfuegbar())) {
      return;
    }

    await this.db.meta.put({ key, value });
  }
}
